import React, { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import CloseIconNew from "../../../../shared/icons/CloseIconNew";
import HintIcon from "../../../../shared/icons/HintIcon";
import style from "./InsuranceExclusionsModal.module.scss";

const exclusionKeys = [
  "glass",
  "cars",
  "alcoholAndTobacco",
  "fruitAndVegetables",
  "fuelAndChemicals",
  "ammunition",
  "drinks",
];

export default function InsuranceExclusionsModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const titleId = useId();
  const descriptionId = useId();
  const dialog = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("button")?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      onClose();
    }
    if (event.key !== "Tab") return;
    const buttons = dialog.current.querySelectorAll("button");
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className={style.overlay}
      data-portal-modal="true"
      onClick={(event) => {
        event.stopPropagation();
        onClose();
      }}
    >
      <div
        ref={dialog}
        className={style.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div className={style.header}>
          <h2 id={titleId}>{t("expeditorInsurance.exclusionsTitle")}</h2>
          <button type="button" className={style.close} onClick={onClose} aria-label={t("common.close")}>
            <CloseIconNew width={24} height={24} />
          </button>
        </div>
        <p id={descriptionId} className={style.description}>
          {t("expeditorInsurance.exclusionsDescription")}
        </p>
        <ol className={style.list}>
          {exclusionKeys.map((key) => (
            <li key={key}>{t(`cargoInsurance.exclusions.${key}`)}</li>
          ))}
        </ol>
        <div className={style.limit}>
          <HintIcon aria-hidden="true" />
          <span>{t("expeditorInsurance.exclusionsLimit")}</span>
        </div>
        <button type="button" className={style.confirm} onClick={onClose}>
          {t("cargoInsurance.understood")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
